import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import type { CountyCoverageParams } from './atoms';

type SetSearchPayload = (
  fn: (current: CountyCoverageParams | undefined) => CountyCoverageParams
) => void;

type DateFilter = CountyCoverageParams['dateFilter'];

/**
 * The page's filter changes: each applies to the search from page 1 and is
 * mirrored in the URL.
 * @param {SetSearchPayload} setSearchPayload Updates the search.
 * @return {object} The date, state, county and reset handlers.
 */
export function useCountyCoverageFilters(setSearchPayload: SetSearchPayload) {
  const history = useHistory();
  const location = useLocation();

  const handleDateFilterChange = useCallback(
    (dateFilter: DateFilter) => {
      setSearchPayload((current) => ({
        ...current,
        page: 1,
        dateFilter,
      }));

      const params = new URLSearchParams(location.search);
      if (dateFilter) {
        params.set('dateFilterType', dateFilter.type);
        if (dateFilter.start) {
          params.set('dateStart', dateFilter.start);
        } else {
          params.delete('dateStart');
        }
        if (dateFilter.end) {
          params.set('dateEnd', dateFilter.end);
        } else {
          params.delete('dateEnd');
        }
      } else {
        params.delete('dateFilterType');
        params.delete('dateStart');
        params.delete('dateEnd');
      }
      params.set('page', '1');
      history.push({ search: params.toString() });
    },
    [location, history, setSearchPayload]
  );

  const handleStateIdsChange = useCallback(
    (stateIds: number[]) => {
      setSearchPayload((current) => ({
        ...current,
        page: 1,
        stateIds: stateIds.length > 0 ? stateIds : undefined,
      }));

      const params = new URLSearchParams(location.search);
      if (stateIds.length > 0) {
        params.set('stateIds', stateIds.join(','));
      } else {
        params.delete('stateIds');
      }
      params.set('page', '1');
      history.push({ search: params.toString() });
    },
    [location, history, setSearchPayload]
  );

  const handleCountyIdsChange = useCallback(
    (countyIds: number[]) => {
      setSearchPayload((current) => ({
        ...current,
        page: 1,
        countyIds: countyIds.length > 0 ? countyIds : undefined,
      }));

      const params = new URLSearchParams(location.search);
      if (countyIds.length > 0) {
        params.set('countyIds', countyIds.join(','));
      } else {
        params.delete('countyIds');
      }
      params.set('page', '1');
      history.push({ search: params.toString() });
    },
    [location, history, setSearchPayload]
  );

  const handleResetFilters = useCallback(() => {
    setSearchPayload((current) => ({
      ...current,
      page: 1,
      dateFilter: undefined,
      stateIds: undefined,
      countyIds: undefined,
    }));

    const params = new URLSearchParams(location.search);
    params.delete('dateFilterType');
    params.delete('dateStart');
    params.delete('dateEnd');
    params.delete('dateFrom');
    params.delete('dateTo');
    params.delete('stateIds');
    params.delete('countyIds');
    params.set('page', '1');
    history.push({ search: params.toString() });
  }, [location, history, setSearchPayload]);

  return {
    handleDateFilterChange,
    handleStateIdsChange,
    handleCountyIdsChange,
    handleResetFilters,
  };
}
